"use client";

import { useMemo } from "react";
import { CurrencyCodeAmount } from "@/components/CurrencyCodeAmount";
import type { StationSummary } from "@/hooks/useStationHubData";
import { formatClassRange } from "@/lib/mobility/bike-facets";
import { summarizeProducts } from "@/lib/mobility/station-data";
import { formatStationLocation } from "@/lib/mobility/station-location";
import { getStationSubtitle } from "@/lib/branches/local-address";
import { useHub } from "./hub-context";
import {
  MetaBadge,
  StationNameLink,
  StationRowShell,
  StationRowSkeleton,
  StationThumb,
} from "./hub-ui";

/**
 * 지점 한 줄 — 목록 · 지역별 섹션 · 모델 시트가 같은 행을 쓴다.
 * 정사각 사진(`StationThumb`) / 이름 / 찾아오는 길 · 도시(한 줄) / [차종 N] [배기량 범위] /
 * 최저가 "/일부터". 배지·가격은 **현재 필터 축을 적용한** 상품 기준이다.
 * 누르면 그 지점의 차종 목록이 바로 열린다. 모바일·태블릿은 오른쪽 아래 "지도" 버튼으로
 * 시트 대신 지도에서 그 지점을 본다(09-29, `StationRowShell`).
 */
export function StationRow({
  station,
  onClick,
}: {
  station: StationSummary;
  onClick: () => void;
}) {
  const {
    t,
    locale,
    data,
    visibleProducts,
    facetsOf,
    formatPrice,
    landmark,
    landmarkLabel,
    rawDistanceKm,
  } = useHub();
  const detail = data.details.get(station.id);
  const products = visibleProducts(station);

  const summary = useMemo(
    () => summarizeProducts(products, facetsOf),
    [products, facetsOf],
  );
  const range = formatClassRange(summary.classKeys, locale);

  // 지점 이름·주소·사진은 상세 조회 결과에만 있다. 오기 전에는 도시명("도쿄도")이나
  // 상품 사진으로 흉내 내지 않고 자리만 잡는다.
  if (!detail) return <StationRowSkeleton />;

  const name = detail.name;
  // 이름 위 위치 줄("일본 도쿄도 시나가와구") + 이름 아래 찾아오는 길(없으면 주소).
  // 도시는 위치 줄에 있으므로 찾아오는 길 뒤에 다시 붙이지 않는다(09-23).
  // 일본 지점(일본어 외 화면)은 "찾아오는 길 · 번역 주소"(10-07, local-address) — 한 줄
  // 말줄임이라 원문은 넣지 않는다(번역 주소가 비었을 때만 원문 폴백).
  const location = formatStationLocation(detail, locale);
  const subtitle = getStationSubtitle({
    locale,
    country: detail.country,
    directions: detail.directions,
    localAddress: detail.addressLocal,
    translatedAddress: detail.address,
  });

  return (
    <StationRowShell
      stationId={station.id}
      name={name}
      onClick={onClick}
      thumb={<StationThumb src={detail.imageUrl} alt={name} />}
    >
      {location ? (
        <p
          className="mb-0.5 truncate text-[12px] text-gray-500 mapShow:text-[13px]"
          suppressHydrationWarning
        >
          {location}
        </p>
      ) : null}
      <StationNameLink
        stationId={station.id}
        className="block min-w-0 truncate text-[15px] font-semibold text-gray-900 mapShow:text-base"
      >
        {name}
      </StationNameLink>
      {subtitle ? (
        <p
          className="mt-0.5 truncate text-[12px] text-gray-500 mapShow:text-[13px]"
          suppressHydrationWarning
        >
          {subtitle}
        </p>
      ) : null}
      {/* 명소를 골랐으면 그 명소까지 직선거리 — 목록이 이 순서로 정렬돼 있다 */}
      {landmark ? (
        <p className="mt-0.5 text-[12px] font-semibold text-orange-700 mapShow:text-[13px]">
          {t("hub.distanceFrom", {
            name: landmarkLabel,
            km: formatKm(rawDistanceKm(station.lat, station.lng)),
          })}
        </p>
      ) : null}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <MetaBadge label={t("hub.bikesN", { n: summary.modelCount })} />
        {range ? <MetaBadge label={range} /> : null}
      </div>
      {summary.minPrice != null ? (
        <p className="mt-1.5 text-[13px] mapShow:text-sm" suppressHydrationWarning>
          <b className="font-bold text-gray-900">
            <CurrencyCodeAmount
              value={formatPrice(summary.minPrice, summary.currency)}
            />
          </b>
          <span className="text-gray-500">{t("hub.fromPerDay")}</span>
        </p>
      ) : null}
    </StationRowShell>
  );
}

/** 1km 미만은 m(10m 단위), 그 이상은 소수 한 자리 km. */
export function formatKm(km: number): string {
  if (km < 1) return `${Math.max(10, Math.round((km * 1000) / 10) * 10)}m`;
  return `${km < 10 ? km.toFixed(1) : Math.round(km)}km`;
}
